import { Link } from '@inertiajs/react';
import { formatDistanceToNow } from 'date-fns';
import { Mail } from 'lucide-react';
// lucide-react v1 dropped its brand icons, so the GitHub mark is the ported
// src/components/icons.tsx one (U1) rather than lucide's.
import { Github } from '@/components/icons';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { SubmissionMemberEmail } from '@/types';

const STATUS_LABELS: Record<string, string> = {
  sent: 'Sent',
  pending: 'Pending',
  failed: 'Failed',
};

function extractGithubUsername(github: string | null | undefined) {
  if (!github) return null;
  // Extract username from GitHub URL (https://github.com/username)
  const match = github.match(/github\.com\/([^/]+)/);
  return match ? match[1] : github;
}

interface EmailsCardProps {
  emails: SubmissionMemberEmail[];
  emptyMessage?: string;
  showRecipient?: boolean;
}

// The "Emails" card the submission page and both hacker profile pages share: every
// OutboundEmail row that reached the person(s) this page is about, linking through to
// the detail page. `showRecipient` hides the per-row GitHub badge on a page that is
// already scoped to one hacker, where it would just repeat the page header.
export function EmailsCard({
  emails,
  emptyMessage = 'No emails sent yet',
  showRecipient = true,
}: EmailsCardProps) {
  if (emails.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Emails
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center text-muted-foreground">
            {emptyMessage}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Emails ({emails.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {emails.map((email) => (
            <Link
              key={email.id}
              href={`/admin/emails/${email.id}`}
              className="block space-y-2 rounded-lg border p-3 transition-colors hover:bg-foreground/5"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-medium text-sm">{email.to}</p>
                    <Badge variant="outline" className="shrink-0 text-xs">
                      {STATUS_LABELS[email.status]}
                    </Badge>
                  </div>
                  {showRecipient && extractGithubUsername(email.recipientGithub) && (
                    <Badge variant="secondary" className="mt-1 w-fit gap-1">
                      <Github className="h-3 w-3" />
                      {extractGithubUsername(email.recipientGithub)}
                    </Badge>
                  )}
                </div>
              </div>
              <p className="truncate text-sm">{email.subject}</p>
              <div className="flex items-center justify-between gap-2 text-muted-foreground text-xs">
                <p>{email.templateName}</p>
                <p>
                  {formatDistanceToNow(new Date(email.createdAt), {
                    addSuffix: true,
                  })}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
